import { useRef } from "react";
import { useReducedMotion } from "@mineoptics/brand/reduced-motion";
import { gsap, ScrollTrigger, SplitText, useGSAP } from "./gsap";
import { introDelay } from "./intro";

export { prefersReducedMotion, useReducedMotion } from "@mineoptics/brand/reduced-motion";
export { clamp01, easeInOut, easeOut, lerp, span } from "@mineoptics/brand/math";

/**
 * Wires the declarative reveal attributes inside the returned ref:
 *
 *   data-split          headline: lines slide up out of a mask
 *   data-fade           block: fades and rises
 *   data-rule           hairline: draws in from the left
 *   data-delay="0.2"    seconds to wait (any of the above)
 *   data-now            play on mount instead of when scrolled into view
 *
 * With reduced motion nothing is hidden and nothing moves.
 */
export function useReveals<T extends HTMLElement = HTMLDivElement>() {
  const scope = useRef<T>(null);
  const reduced = useReducedMotion();

  useGSAP(
    () => {
      const root = scope.current;
      if (!root || reduced) return;

      const trigger = (el: Element) =>
        el.hasAttribute("data-now") ? undefined : { trigger: el, start: "top 90%", once: true };
      // Elements that play on load wait for the intro overlay, when it is shown.
      const delay = (el: Element) =>
        Number(el.getAttribute("data-delay") ?? 0) + (el.hasAttribute("data-now") ? introDelay() : 0);

      const splits = gsap.utils.toArray<HTMLElement>("[data-split]", root).map((el) =>
        SplitText.create(el, {
          type: "lines",
          mask: "lines",
          linesClass: "split-line",
          autoSplit: true,
          onSplit: (self) =>
            gsap.from(self.lines, {
              yPercent: 115,
              duration: 1.15,
              ease: "expo.out",
              stagger: 0.085,
              delay: delay(el),
              scrollTrigger: trigger(el),
            }),
        }),
      );

      gsap.utils.toArray<HTMLElement>("[data-fade]", root).forEach((el) => {
        gsap.from(el, {
          y: 28,
          autoAlpha: 0,
          duration: 1.1,
          ease: "power3.out",
          delay: delay(el),
          scrollTrigger: trigger(el),
        });
      });

      gsap.utils.toArray<HTMLElement>("[data-rule]", root).forEach((el) => {
        gsap.from(el, {
          scaleX: 0,
          duration: 1.3,
          ease: "expo.out",
          delay: delay(el),
          scrollTrigger: trigger(el),
        });
      });

      // Pinned sections created by child components change the page height;
      // measure the reveals after them.
      ScrollTrigger.sort();
      ScrollTrigger.refresh();

      return () => splits.forEach((split) => split.revert());
    },
    { scope, dependencies: [reduced] },
  );

  return scope;
}
